import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Lock, ShieldCheck, Loader2, Check, Laptop, Smartphone, Monitor, QrCode, Copy, ArrowLeft } from 'lucide-react';
import { useSectionTranslation } from '@/hooks';
import { api, isApiError } from '@/lib/api';
import { settingsTranslations } from './translations';

const pwSchema = z.object({
  currentPassword: z.string().min(1, 'Required'),
  newPassword: z.string().min(8, 'Min 8 chars').regex(/[a-z]/, 'Lowercase').regex(/[A-Z]/, 'Uppercase').regex(/[0-9]/, 'Number'),
  confirmPassword: z.string(),
}).refine((d) => d.newPassword === d.confirmPassword, { message: 'Passwords do not match', path: ['confirmPassword'] });

type PwForm = z.infer<typeof pwSchema>;

interface SessionInfo {
  deviceName?: string;
  deviceType?: string;
  ip?: string;
  browser?: string;
  os?: string;
  lastUsedAt: string;
  tokenId: string;
  isCurrent?: boolean;
}

interface SessionsResponse {
  sessions: SessionInfo[];
  total: number;
}

export function SecuritySettings() {
  const t = useSectionTranslation(settingsTranslations);
  const [pwSaving, setPwSaving] = useState(false);
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [totalSessions, setTotalSessions] = useState(0);
  const [revoking, setRevoking] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [sessionsError, setSessionsError] = useState<string | null>(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<PwForm>({
    resolver: zodResolver(pwSchema),
  });

  useEffect(() => {
    api.get<SessionsResponse>('/auth/sessions').then((res) => {
      if (isApiError(res)) {
        setSessionsError(t.security.sessions.error);
        return;
      }
      setSessions(res.data?.sessions ?? []);
      setTotalSessions(res.data?.total ?? 0);
    });
  }, [t.security.sessions.error]);

  const onPasswordSubmit = handleSubmit(async (values) => {
    setPwSaving(true);
    setPwError(null);
    setPwSuccess(false);
    const res = await api.post('/auth/change-password', values);
    setPwSaving(false);
    if (isApiError(res)) {
      setPwError(t.security.password.error);
    } else {
      setPwSuccess(true);
      reset();
      setTimeout(() => setPwSuccess(false), 3000);
    }
  });

  const onRevokeAll = async () => {
    setRevoking(true);
    const res = await api.post('/auth/sessions/revoke-all');
    setRevoking(false);
    if (isApiError(res)) {
      setSessionsError(t.security.sessions.error);
      return;
    }
    setSessions([]);
    setTotalSessions(0);
  };

  const onRevokeSession = async (tokenId: string) => {
    setRevokingId(tokenId);
    setSessionsError(null);
    const res = await api.post(`/auth/sessions/${tokenId}/revoke`);
    setRevokingId(null);
    if (isApiError(res)) {
      setSessionsError(res.error?.message || t.security.sessions.error);
      return;
    }
    setSessions((prev) => prev.filter((s) => s.tokenId !== tokenId));
    setTotalSessions((n) => Math.max(0, n - 1));
  };

  return (
    <div className="space-y-10 max-w-xl">
      {/* Change password */}
      <div>
        <h2 className="text-lg font-semibold text-brand-white mb-1">{t.security.password.title}</h2>
        <p className="text-sm text-brand-muted mb-5">{t.security.password.desc}</p>

        <form onSubmit={onPasswordSubmit} className="space-y-4">
          {pwSuccess && (
            <div className="flex items-center gap-2 rounded-lg border border-brand-success/30 bg-brand-success/10 px-3 py-2.5 text-sm text-brand-success">
              <Check size={14} />{t.security.password.success}
            </div>
          )}
          {pwError && (
            <div className="flex items-center gap-2 rounded-lg border border-brand-danger/30 bg-brand-danger/10 px-3 py-2.5 text-sm text-brand-danger">
              {pwError}
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-brand-muted block mb-1.5">{t.security.password.current}</label>
            <div className="flex items-center rounded-lg bg-surface-card border border-surface-border focus-within:border-accent/30 transition-colors">
              <Lock size={14} className="ms-3 text-brand-muted" />
              <input type="password" {...register('currentPassword')} className="flex-1 bg-transparent px-3 py-2.5 text-sm text-brand-white focus:outline-none" />
            </div>
            {errors.currentPassword && <p className="text-xs text-brand-danger mt-1">{errors.currentPassword.message}</p>}
          </div>

          <div>
            <label className="text-xs font-medium text-brand-muted block mb-1.5">{t.security.password.newPassword}</label>
            <div className="flex items-center rounded-lg bg-surface-card border border-surface-border focus-within:border-accent/30 transition-colors">
              <Lock size={14} className="ms-3 text-brand-muted" />
              <input type="password" {...register('newPassword')} className="flex-1 bg-transparent px-3 py-2.5 text-sm text-brand-white focus:outline-none" />
            </div>
            {errors.newPassword && <p className="text-xs text-brand-danger mt-1">{errors.newPassword.message}</p>}
          </div>

          <div>
            <label className="text-xs font-medium text-brand-muted block mb-1.5">{t.security.password.confirmPassword}</label>
            <div className="flex items-center rounded-lg bg-surface-card border border-surface-border focus-within:border-accent/30 transition-colors">
              <Lock size={14} className="ms-3 text-brand-muted" />
              <input type="password" {...register('confirmPassword')} className="flex-1 bg-transparent px-3 py-2.5 text-sm text-brand-white focus:outline-none" />
            </div>
            {errors.confirmPassword && <p className="text-xs text-brand-danger mt-1">{errors.confirmPassword.message}</p>}
          </div>

          <button type="submit" disabled={pwSaving} className="btn-primary h-10 disabled:opacity-60">
            {pwSaving ? <><Loader2 size={15} className="animate-spin" />{t.security.password.submitting}</> : t.security.password.submit}
          </button>
        </form>
      </div>

      {/* 2FA */}
      <TwoFactorSection />

      {/* Active sessions */}
      <div>
        <h2 className="text-lg font-semibold text-brand-white mb-1">{t.security.sessions.title}</h2>
        <p className="text-sm text-brand-muted mb-4">{t.security.sessions.desc}</p>

        {sessionsError && (
          <p role="alert" className="mb-3 text-sm text-brand-danger">{sessionsError}</p>
        )}

        <div className="space-y-2">
          {sessions.map((s) => (
            <div key={s.tokenId} className="flex items-center gap-3 rounded-lg bg-surface-card border border-surface-border px-4 py-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-hover text-brand-muted">
                {s.deviceType === 'mobile' ? <Smartphone size={16} /> : s.deviceType === 'tablet' ? <Monitor size={16} /> : <Laptop size={16} />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-brand-white truncate">{s.deviceName || s.browser || 'Unknown'}</p>
                <p className="text-[11px] text-brand-muted">{s.os} · {s.ip}</p>
              </div>
              {s.isCurrent && (
                <span className="text-[10px] text-brand-success font-medium">{t.security.sessions.current}</span>
              )}
              {!s.isCurrent && (
                <button
                  type="button"
                  onClick={() => onRevokeSession(s.tokenId)}
                  disabled={revokingId === s.tokenId}
                  aria-label={t.security.sessions.revoke}
                  className="h-8 px-3 rounded-lg border border-surface-border text-xs font-medium text-brand-muted hover:border-brand-danger/30 hover:text-brand-danger transition-colors disabled:opacity-60"
                >
                  {revokingId === s.tokenId ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    t.security.sessions.revoke
                  )}
                </button>
              )}
            </div>
          ))}
          {sessions.length === 0 && (
            <p className="text-sm text-brand-muted">{t.security.sessions.empty}</p>
          )}
        </div>

        {totalSessions > sessions.length && (
          <p className="mt-3 text-xs text-brand-muted">
            {t.security.sessions.showingXofY
              .replace('{shown}', String(sessions.length))
              .replace('{total}', String(totalSessions))}
          </p>
        )}

        {sessions.length > 1 && (
          <button
            type="button"
            onClick={onRevokeAll}
            disabled={revoking}
            className="mt-3 btn-secondary h-9 text-brand-danger border-brand-danger/20 hover:bg-brand-danger/5 disabled:opacity-60"
          >
            {revoking ? <><Loader2 size={14} className="animate-spin" />{t.security.sessions.loggingOut}</> : t.security.sessions.logoutAll}
          </button>
        )}
      </div>
    </div>
  );
}

/** Inline 2FA section — multi-step setup flow. */
function TwoFactorSection() {
  const t = useSectionTranslation(settingsTranslations);
  const [step, setStep] = useState<'idle' | 'setup' | 'enabled'>('idle');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [manualSecret, setManualSecret] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get<{ enabled: boolean }>('/auth/2fa/status').then((res) => {
      if (!isApiError(res)) setStep(res.data.enabled ? 'enabled' : 'idle');
    });
  }, []);

  const generate = async () => {
    setLoading(true);
    setError('');
    const res = await api.post<{ qrDataUrl: string; secret: string }>('/auth/2fa/generate');
    setLoading(false);
    if (!isApiError(res)) {
      setQrDataUrl(res.data.qrDataUrl);
      setManualSecret(res.data.secret);
      setCode('');
      setStep('setup');
    }
  };

  const verify = async () => {
    setLoading(true);
    setError('');
    const res = await api.post<{ enabled: boolean }>('/auth/2fa/enable', { code });
    setLoading(false);
    if (isApiError(res)) {
      setError(res.error.message || 'Invalid code');
    } else {
      setStep('enabled');
    }
  };

  const disable = async () => {
    setLoading(true);
    const res = await api.post('/auth/2fa/disable');
    setLoading(false);
    if (!isApiError(res)) setStep('idle');
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-brand-white mb-1">{t.security.twoFactor.title}</h2>

      {step === 'idle' && (
        <>
          <p className="text-sm text-brand-muted mb-4">{t.security.twoFactor.desc}</p>
          <button onClick={generate} disabled={loading} className="btn-primary h-10 disabled:opacity-60">
            {loading ? <><Loader2 size={15} className="animate-spin" />{t.security.twoFactor.enable}</> : <><ShieldCheck size={15} />{t.security.twoFactor.enable}</>}
          </button>
        </>
      )}

      {step === 'setup' && (
        <div className="space-y-5">
          <button onClick={() => setStep('idle')} className="flex items-center gap-1.5 text-xs text-brand-muted hover:text-brand-white transition-colors">
            <ArrowLeft size={13} />{t.security.twoFactor.cancel}
          </button>

          {/* Step 1: QR */}
          <div>
            <p className="text-sm font-medium text-brand-white mb-1">{t.security.twoFactor.step1}</p>
            <p className="text-xs text-brand-muted mb-3">{t.security.twoFactor.step1Desc}</p>
            {qrDataUrl && (
              <img src={qrDataUrl} alt="TOTP QR code" className="h-48 w-48 rounded-xl bg-white p-2" />
            )}
            {manualSecret && (
              <div className="mt-3 flex items-center gap-2">
                <code className="text-xs text-brand-muted bg-surface-hover px-2 py-1 rounded">{manualSecret}</code>
                <button
                  onClick={() => { navigator.clipboard.writeText(manualSecret); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                  className="text-xs text-accent hover:underline"
                >
                  {copied ? <Check size={13} /> : <Copy size={13} />}
                </button>
              </div>
            )}
          </div>

          {/* Step 2: Verify */}
          <div>
            <p className="text-sm font-medium text-brand-white mb-1">{t.security.twoFactor.step2}</p>
            <p className="text-xs text-brand-muted mb-3">{t.security.twoFactor.step2Desc}</p>
            <div className="flex items-center gap-3">
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="w-28 rounded-lg bg-surface-card border border-surface-border px-3 py-2 text-center text-lg font-mono text-brand-white tracking-[0.3em] focus:outline-none focus:border-accent/30 transition-colors"
              />
              <button onClick={verify} disabled={loading || code.length !== 6} className="btn-primary h-10 disabled:opacity-60">
                {loading ? <><Loader2 size={15} className="animate-spin" />{t.security.twoFactor.verifying}</> : <><QrCode size={15} />{t.security.twoFactor.verify}</>}
              </button>
            </div>
            {error && <p className="text-xs text-brand-danger mt-2">{error}</p>}
          </div>
        </div>
      )}

      {step === 'enabled' && (
        <>
          <p className="text-sm text-brand-muted mb-1">{t.security.twoFactor.enabledDesc}</p>
          <div className="flex items-center gap-2 mt-1 mb-4">
            <span className="h-2 w-2 rounded-full bg-brand-success" />
            <span className="text-sm font-medium text-brand-success">{t.security.twoFactor.enabled}</span>
          </div>
          <button onClick={disable} disabled={loading} className="btn-secondary h-10 text-brand-danger border-brand-danger/20 hover:bg-brand-danger/5 disabled:opacity-60">
            {loading ? <><Loader2 size={15} className="animate-spin" />{t.security.twoFactor.disabling}</> : t.security.twoFactor.disable}
          </button>
        </>
      )}
    </div>
  );
}